"use client";

import { useRouter } from "next/navigation";
import { useTransition } from "react";

import { Button } from "@/components/ui/button";

/**
 * "Tentar novamente" for an error boundary (`error.tsx`).
 *
 * `router.refresh()` **and** `reset()`, in the same transition: `reset()` alone remounts the
 * boundary against the cached RSC payload — the one that failed — and `refresh()` alone
 * fetches a new tree that the boundary keeps hiding behind the error.
 *
 * Without a boundary (the same screen rendered as a state by a server page) there is nothing to
 * reset: `refresh()` alone re-runs the page.
 */
/**
 * The row of an error screen's two actions. Side by side they need ~440px, more than a phone
 * has, and neither label may wrap (`whitespace-nowrap`): below 520px they stack, full width.
 */
export const ERROR_ACTIONS_CLASS =
  "mt-8 flex w-full max-w-[20rem] flex-col gap-3 [@media(min-width:520px)]:w-auto [@media(min-width:520px)]:max-w-none [@media(min-width:520px)]:flex-row [@media(min-width:520px)]:gap-4";

export function ErrorRetryButton({ reset }: { reset?: () => void }) {
  const router = useRouter();
  const [isRetrying, startRetry] = useTransition();

  return (
    <Button
      variant="outline"
      aria-busy={isRetrying}
      /*
       * Fixed label: the button shares a row with the escape one, so swapping the text would
       * change its width and shift the neighbour at tap time. `aria-busy` and the cursor are
       * what report the pending state.
       */
      className="aria-busy:cursor-progress"
      onClick={() =>
        startRetry(() => {
          router.refresh();
          reset?.();
        })
      }
    >
      Tentar novamente
    </Button>
  );
}
